/**
 * **What the COMBAT.app board's terrain MEANS — the LEGEND window's contents, as pure data.**
 *
 * Since `terrain-tile-art` 5.3 the board paints each tile as its own art (`decisions/0184`), so the
 * key lists **tiles**, not marks: one row per catalogue tile on the board, each with its painted
 * swatch, name and property chips, one row per custom property set, and then the carriers that are
 * not tiles — the wall classes, placed objects, and area-effect clouds. The per-mark rows (cover
 * borders, hazard glyphs, the pit well, the stipple) went with the marks themselves.
 *
 * ## Presence-driven, not a static key
 *
 * The legend names **only what this board actually draws**, the principle `cli/src/mapImage.ts`'s
 * `mapImageLegend` states for the PNG legend: *"a static legend teaches the reader to skim past it;
 * one that only ever names present features stays worth reading, and its absences are themselves
 * informative."* {@link legendTilesFor} and {@link legendFor} are exported for the same reason
 * `mapImageLegend` is: a test can check that an absent row is genuinely absent without decoding a
 * canvas.
 *
 * ## The legend reports what is DRAWN, not what is authored
 *
 * Wall rows come from the renderer's own fold ({@link edgeTreatment}), so the key never promises a
 * wall the GM cannot find: an edge carrying only a cover grade draws nothing, and earns no row.
 * Custom cells group by the painter's own key (`terrainArt/geometry.ts`'s `regionKey`), so two cells
 * the board paints as one region are one row.
 *
 * @module
 */
import { CUSTOM_TILE, TERRAIN_TILES, TILE_TYPE_IDS, isCharacter, tileProperties, type EncounterState, type TerrainCell, type TileTypeId } from "@lancer-vtt/domain";
import { CUSTOM_TERRAIN_NAME, tileChips } from "./terrainTiles";
import { SCENERY_TOKEN_LOOK, edgeTreatment, type TokenTreatment } from "./combatCells";
import { regionKey } from "./terrainArt/geometry";
import { AREA_EFFECT_STYLE, EDGE_VISUALS, type AreaEffectStyle, type EdgeVisual } from "./terrainPalette";

/**
 * One non-tile legend row's stable identity: a wall class, the object token, or the area cloud. A
 * mark on the board with no id here is a mark the legend cannot explain, which is why the edge
 * enumeration is walked rather than retyped.
 */
export type LegendClassId = EdgeClassId | "object" | "area";

/**
 * The row id for one edge wall class — **derived from the palette's own {@link EdgeVisual}**, so a
 * new wall class is one edit in `terrainPalette.ts` and {@link LEGEND_COPY}'s `satisfies` forces
 * someone to write what it means before it can ship.
 */
export type EdgeClassId = `wall-${EdgeVisual}`;

/** An edge class's row id. Never spell one of these out by hand. */
export function edgeClassId(visual: EdgeVisual): EdgeClassId {
  return `wall-${visual}`;
}

/** Every {@link LegendClassId}, in the order {@link legendFor} emits them. */
export const LEGEND_CLASS_IDS: readonly LegendClassId[] = [...EDGE_VISUALS.map(edgeClassId), "object", "area"];

/**
 * The sample a non-tile row draws. A wall's is painted by the terrain painter
 * (`terrainArt/swatches.ts`), so the row shows the wall the board draws; the object and the cloud
 * are drawn from their palette specs.
 */
export type LegendSwatch = { carrier: "edge"; visual: EdgeVisual } | { carrier: "object"; look: TokenTreatment } | { carrier: "area"; style: AreaEffectStyle };

/** One non-tile row: a stable id, the name of the mark, what it does to play, and its sample. */
export type LegendItem = {
  id: LegendClassId;
  /** The mark's short name — what a GM would call it out loud. */
  label: string;
  /**
   * What the mark means **mechanically**, phrased as consequence rather than as the flag that
   * produced it (`terrain-legibility` D3: never raw flag names), in the register the TERRAIN rail's
   * own `title=` strings speak. **Cover is DIFFICULTY, never a flat modifier** (Core p.66, p.13):
   * the rules-copy pin (`rulesCopy.pin.test.ts`) sweeps this table.
   */
  detail: string;
  swatch: LegendSwatch;
};

/**
 * The wording per non-tile class. `satisfies Record<LegendClassId, …>` is the completeness pin: a
 * new class cannot reach the legend without copy. The hovered-tile pane reads the `area` entry too
 * (`terrainCellDescription.ts`), so the two surfaces describe a cloud in the same words.
 */
export const LEGEND_COPY = {
  "wall-full": {
    label: "Full wall",
    detail: "On the line between two cells. Blocks movement, sight, and flight.",
  },
  // The three low walls: one barrier at three cover grades, which drew IDENTICALLY until 2026-09-01,
  // so an authored grade was invisible on the board. The stroke now carries it in the same alphabet
  // as the cell cover border — dotted grants nothing, dashed grants soft, solid grants hard.
  //
  // **The RAW footing differs between the two graded rows, and the copy says so rather than
  // flattening it.** Core p.66 lists what grants each grade, and low walls appear in the SOFT list
  // verbatim: "Soft cover includes smoke, foliage, trees, blinding light, dust clouds, low hills, and
  // low walls." Its hard-cover list is "ruined buildings, tall walls, bulkheads, reinforced
  // emplacements, and destroyed mechs and vehicles" — nothing you step over, and the book gives no
  // general rule granting hard cover from something passable. So the soft row cites the book; the
  // hard row names the grade as the map author's, which is what it is.
  "wall-low": {
    label: "Low wall",
    detail: "A dotted line between two cells. Blocks ground movement only — flyable, sight passes — and grants no cover, so a shot across it is unmodified.",
  },
  "wall-low-soft": {
    label: "Low wall — soft cover",
    detail: "A dashed line between two cells. Blocks ground movement only — flyable, sight passes — and a target behind it has soft cover: +1 Difficulty, subtract 1d6. Core p.66 lists low walls among the things that grant soft cover.",
  },
  "wall-low-hard": {
    label: "Low wall — hard cover",
    detail: "A solid line between two cells. Blocks ground movement only — flyable, sight passes — and this board grades a target behind it as being in hard cover: +2 Difficulty, subtract the highest of 2d6. That grade is the map author's own choice, not the book's — Core p.66 lists low walls under soft cover.",
  },
  object: {
    label: "Object",
    detail: "A placed crate, boulder or wall section. What it blocks, and the cover it grants, come from the object itself rather than the ground under it — and it can be destroyed.",
  },
  area: {
    label: "Area effect",
    detail: "A standing cloud. Anyone inside it has soft cover, and it softens any sightline drawn through it.",
  },
} as const satisfies Record<LegendClassId, { label: string; detail: string }>;

/** Assemble one row from its class id and its sample. */
function row(id: LegendClassId, swatch: LegendSwatch): LegendItem {
  return { id, label: LEGEND_COPY[id].label, detail: LEGEND_COPY[id].detail, swatch };
}

/**
 * The non-tile rows for `state` — the wall classes, objects and clouds the board is drawing, and
 * nothing else, walls first in the palette's order.
 */
export function legendFor(state: EncounterState): LegendItem[] {
  const items: LegendItem[] = [];
  const walls = new Set<EdgeVisual>();
  for (const flags of Object.values(state.edges)) {
    const treatment = edgeTreatment(flags);
    if (treatment !== null) walls.add(treatment);
  }
  for (const visual of EDGE_VISUALS) {
    if (walls.has(visual)) items.push(row(edgeClassId(visual), { carrier: "edge", visual }));
  }

  // Objects and deployables are the third obstacle carrier, and they are combatants — so the question
  // is whether one is ON the board, not merely in the encounter. An unplaced crate sitting in the
  // roster draws nothing, and a legend row for it would send the GM hunting a diamond that isn't
  // there. `isCharacter` is the canonical split (capabilities over kinds), so a drone reads as a unit
  // rather than as scenery, exactly as `tokenLook` decides its silhouette.
  if (Object.values(state.combatants).some((c) => c.pos != null && !isCharacter(c))) {
    items.push(row("object", { carrier: "object", look: SCENERY_TOKEN_LOOK }));
  }

  if (Object.keys(state.areaEffects ?? {}).length > 0) {
    items.push(row("area", { carrier: "area", style: AREA_EFFECT_STYLE }));
  }

  return items;
}

/**
 * One tile present on the board — a row of the Map key's tile list. A catalogue tile has one
 * row; custom cells have one row per property set, keyed like the painter's regions. `cell` is what
 * the row's swatch paints; `chips` are the property chips the TERRAIN palette prints.
 */
export type LegendTile = {
  key: string;
  id: TileTypeId | typeof CUSTOM_TILE;
  name: string;
  chips: string[];
  /** How many cells on the board carry it. */
  count: number;
  cell: TerrainCell;
};

/**
 * The tiles painted on `state`'s board — presence-driven, so a tile the map doesn't use never
 * appears. Catalogue order, then each custom property set, most cells first.
 */
export function legendTilesFor(state: EncounterState): LegendTile[] {
  const byKey = new Map<string, { cell: TerrainCell; count: number }>();
  for (const cell of Object.values(state.terrain)) {
    const key = regionKey(cell);
    const seen = byKey.get(key);
    if (seen) seen.count++;
    else byKey.set(key, { cell, count: 1 });
  }
  const rows: LegendTile[] = [];
  for (const id of TILE_TYPE_IDS) {
    const hit = byKey.get(id);
    if (hit) rows.push({ key: id, id, name: TERRAIN_TILES[id].name, chips: tileChips(TERRAIN_TILES[id].properties), count: hit.count, cell: hit.cell });
  }
  const custom = [...byKey.entries()].filter(([, v]) => v.cell.tile === CUSTOM_TILE);
  custom.sort(([ka, a], [kb, b]) => b.count - a.count || (ka < kb ? -1 : 1));
  for (const [key, { cell, count }] of custom) {
    rows.push({ key, id: CUSTOM_TILE, name: CUSTOM_TERRAIN_NAME, chips: tileChips(tileProperties(cell)), count, cell });
  }
  return rows;
}
